'use client'

import Link from 'next/link'
import { Badge } from '@/components/ui/badge'
import type { InboxCard } from '@/types/dashboard'

interface InboxPreviewProps {
  items: InboxCard[]
}

export function InboxPreview({ items }: InboxPreviewProps) {
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 py-10 text-center">
        <p className="text-sm font-medium text-gray-600">Inbox 항목이 없습니다.</p>
        <p className="mt-1 text-xs text-gray-500">새로운 초대/요청/견적/알림이 도착하면 여기에 표시돼요.</p>
      </div>
    )
  }

  const getTypeBadge = (type?: string) => {
    const t = (type ?? '').toUpperCase()

    // 기본은 회색, 유형별로 은은하게만 구분
    const base = 'border-gray-200 bg-gray-50 text-gray-700'
    const blue = 'border-blue-200 bg-blue-50 text-blue-700'
    const purple = 'border-purple-200 bg-purple-50 text-purple-700'
    const amber = 'border-amber-200 bg-amber-50 text-amber-800'

    if (t === 'INVITE' || t === 'TEAM_INVITE') return { label: '팀 초대', className: purple }
    if (t === 'REQUEST' || t === 'ESTIMATE_REQUEST') return { label: '요청', className: amber }
    if (t === 'QUOTE' || t === 'ESTIMATE_SUBMITTED') return { label: '견적', className: blue }
    return { label: '알림', className: base }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return '오늘'
    if (diffDays === 1) return '어제'
    if (diffDays < 7) return `${diffDays}일 전`

    return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const typeBadge = getTypeBadge(item.type)

        return (
          <Link
            key={item.id}
            href={item.href}
            className={[
              'group flex items-center justify-between gap-4 rounded-2xl border bg-white px-4 py-3 shadow-sm transition-all',
              item.isUnread ? 'border-blue-200/70' : 'border-gray-200',
              'hover:border-gray-300 hover:shadow-md',
            ].join(' ')}
          >
            {/* Left */}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={`text-xs font-semibold ${typeBadge.className}`}>
                  {typeBadge.label}
                </Badge>

                {item.isUnread && (
                  <Badge
                    variant="outline"
                    className="border-blue-200 bg-blue-50 text-xs font-semibold text-blue-700"
                  >
                    미확인
                  </Badge>
                )}
              </div>

              <p className="mt-1 truncate text-sm font-bold text-gray-900 group-hover:text-blue-700">
                {item.title}
              </p>
            </div>

            {/* Right */}
            <div className="shrink-0 text-right">
              {item.createdAt ? (
                <p className="text-xs text-gray-500">{formatDate(item.createdAt)}</p>
              ) : (
                <p className="text-xs text-gray-400">-</p>
              )}
              <span className="mt-1 block text-xs font-semibold text-gray-500 group-hover:text-blue-700">
                확인 →
              </span>
            </div>
          </Link>
        )
      })}
    </div>
  )
}
